import React, { useEffect } from "react";
import confetti from "canvas-confetti";
import { motion, AnimatePresence } from "framer-motion";
import { useApp } from "@/context/AppContext";
import { formatTime } from "@/lib/game/numberPlacement";
import { Trophy, Clock, Lightbulb, RotateCcw, Home, BarChart3 } from "lucide-react";

export const WinModal = ({ open, mode, difficulty, timeMs, hintsUsed, onPlayAgain, onHome, onStats }) => {
  const { t } = useApp();

  useEffect(() => {
    if (!open) return;
    const fire = (particleRatio, opts) =>
      confetti({
        origin: { y: 0.6 },
        colors: ["#FF5A5F", "#FFC043", "#4CAF50", "#111111"],
        ...opts,
        particleCount: Math.floor(200 * particleRatio),
      });
    fire(0.25, { spread: 26, startVelocity: 55 });
    fire(0.2, { spread: 60 });
    fire(0.35, { spread: 100, decay: 0.91, scalar: 0.9 });
    fire(0.1, { spread: 120, startVelocity: 25, decay: 0.92, scalar: 1.2 });
  }, [open]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="win-modal"
          className="fixed inset-0 z-50 grid place-items-center p-4 bg-ink/40 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            initial={{ scale: 0.8, y: 30, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ type: "spring", stiffness: 260, damping: 20 }}
            className="w-full max-w-sm bg-cream border-2 border-ink rounded-2xl shadow-brutal-xl p-6 text-center"
          >
            <motion.div
              initial={{ rotate: -12, scale: 0 }}
              animate={{ rotate: 0, scale: 1 }}
              transition={{ delay: 0.1, type: "spring", stiffness: 200 }}
              className="mx-auto grid place-items-center w-16 h-16 rounded-full bg-sunny border-2 border-ink shadow-brutal mb-4"
            >
              <Trophy className="w-8 h-8 text-ink" strokeWidth={2.5} />
            </motion.div>

            <h2 className="font-display font-black text-3xl tracking-tight">{t("win_title")}</h2>
            <p className="text-sm font-medium text-ink/60 mt-1">
              {t("win_sub")} · {t(difficulty)} · {mode === "daily" ? t("mode_daily") : t("mode_endless")}
            </p>

            <div className="grid grid-cols-2 gap-3 my-5">
              <div className="rounded-xl border-2 border-ink bg-white p-3 shadow-brutal-sm">
                <Clock className="w-4 h-4 mx-auto mb-1" strokeWidth={2.5} />
                <div className="font-mono font-extrabold text-xl tabular-nums" data-testid="win-time">
                  {formatTime(timeMs)}
                </div>
                <div className="text-[11px] font-bold uppercase tracking-wider text-ink/50">{t("win_time")}</div>
              </div>
              <div className="rounded-xl border-2 border-ink bg-white p-3 shadow-brutal-sm">
                <Lightbulb className="w-4 h-4 mx-auto mb-1" strokeWidth={2.5} />
                <div className="font-mono font-extrabold text-xl tabular-nums" data-testid="win-hints">
                  {hintsUsed}
                </div>
                <div className="text-[11px] font-bold uppercase tracking-wider text-ink/50">{t("win_hints")}</div>
              </div>
            </div>

            <div className="flex flex-col gap-2.5">
              <button
                data-testid="play-again-btn"
                onClick={onPlayAgain}
                className="inline-flex items-center justify-center gap-2 w-full px-5 py-3 rounded-lg bg-coral text-white border-2 border-ink font-display font-bold shadow-brutal hover:shadow-brutal-lg hover:-translate-y-0.5 transition-all"
              >
                <RotateCcw className="w-4 h-4" strokeWidth={2.5} /> {t("play_again")}
              </button>
              <div className="flex gap-2.5">
                <button
                  data-testid="view-stats-btn"
                  onClick={onStats}
                  className="inline-flex items-center justify-center gap-2 flex-1 px-4 py-2.5 rounded-lg bg-white text-ink border-2 border-ink font-display font-bold shadow-brutal-sm hover:-translate-y-0.5 transition-transform"
                >
                  <BarChart3 className="w-4 h-4" strokeWidth={2.5} /> {t("nav_stats")}
                </button>
                <button
                  data-testid="back-home-btn"
                  onClick={onHome}
                  className="inline-flex items-center justify-center gap-2 flex-1 px-4 py-2.5 rounded-lg bg-white text-ink border-2 border-ink font-display font-bold shadow-brutal-sm hover:-translate-y-0.5 transition-transform"
                >
                  <Home className="w-4 h-4" strokeWidth={2.5} /> {t("back_home")}
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
